import { useEffect, useState } from 'react';
import './App.css';

const INSTAGRAM_URL = 'https://instagram.com/linja.app';

function App() {
  const [showInstagramPopup, setShowInstagramPopup] = useState(false);
  const normalizedPath = window.location.pathname.toLowerCase().replace(/\/$/, '') || '/';
  const isPrivacyPage = normalizedPath === '/privacy';
  const isTermsPage = normalizedPath === '/terms-of-use';
  const isDeleteAccountPage = normalizedPath === '/delete-account';
  const normalizedPublicUrl =
    !process.env.PUBLIC_URL || process.env.PUBLIC_URL === '.'
      ? ''
      : process.env.PUBLIC_URL.replace(/\/$/, '');
  const assetPath = (path) => `${normalizedPublicUrl}${path.startsWith('/') ? path : `/${path}`}`;

  useEffect(() => {
    if (isPrivacyPage || isTermsPage || isDeleteAccountPage) return;
    const popupSeen = window.localStorage.getItem('linja_insta_popup_seen');
    if (!popupSeen) setShowInstagramPopup(true);
  }, [isPrivacyPage, isTermsPage, isDeleteAccountPage]);

  const closeInstagramPopup = () => {
    setShowInstagramPopup(false);
    window.localStorage.setItem('linja_insta_popup_seen', '1');
  };

  const features = [
    {
      title: 'Bus en temps reel',
      text: 'Visualisez tous les bus TanGo de la metropole de Nimes en direct pour savoir exactement quand partir.',
    },
    {
      title: 'Information instantanee',
      text: 'Consultez en un geste les prochains passages et les lignes actives a Nimes et dans les villages voisins.',
    },
    {
      title: 'Interface ultra simple',
      text: "Linja est pensee pour aller droit a l'essentiel, sans menus compliques ni distractions.",
    },
  ];

  const steps = [
    'Choisissez votre ligne TanGo ou votre arret en quelques secondes.',
    'Suivez les bus en direct sur la carte et comparez les prochains passages.',
    'Partez au bon moment et ne ratez plus votre trajet.',
  ];

  const faqs = [
    {
      question: 'Linja est-elle une application officielle TanGo ?',
      answer:
        "Linja est une application independante, creee pour offrir une experience plus rapide et plus simple pour suivre les bus de la ville.",
    },
    {
      question: 'Puis-je utiliser Linja gratuitement ?',
      answer: 'Oui, la consultation des lignes et des passages en temps reel est accessible gratuitement.',
    },
    {
      question: 'Quelle zone est couverte par Linja ?',
      answer: 'Linja couvre uniquement Nimes et les villages autour, avec l ensemble des bus de la metropole de Nimes.',
    },
  ];

  const legalLinks = (
    <div className="footer-links">
      <a href="/">Accueil</a>
      <a href="/privacy">Politique de confidentialite</a>
      <a href="/terms-of-use">Terms of Use</a>
      <a href="/delete-account">Supprimer mon compte</a>
    </div>
  );

  if (isPrivacyPage) {
    return (
      <div className="app-shell">
        <div className="ambient-shape ambient-left" />
        <div className="ambient-shape ambient-right" />

        <header className="topbar">
          <a className="brand" href="/">
            <img src={assetPath('/logo/logo-rounded.png')} alt="Logo Linja" />
            <span>Linja</span>
          </a>
          <nav className="topbar-nav">
            <a href="/">Retour a l'accueil</a>
          </nav>
        </header>

        <main className="privacy-page">
          <article className="privacy-card">
            <h1>Politique de confidentialite</h1>
            <p className="privacy-updated">
              <strong>Derniere mise a jour :</strong> 31/03/26
            </p>
            <p>
              La presente politique de confidentialite decrit la maniere dont <strong>FLEM</strong> ("nous")
              collecte, utilise et protege les donnees personnelles des utilisateurs de l'application{' '}
              <strong>Linja</strong> ("l'Application").
            </p>
            <p>En utilisant l'Application, vous acceptez les pratiques decrites dans cette politique.</p>

            <section className="privacy-section">
              <h2>1. Donnees que nous collectons</h2>
              <p>Selon les fonctionnalites utilisees, nous pouvons collecter les categories suivantes :</p>
              <ul>
                <li>
                  <strong>Donnees de localisation</strong> (si vous l'autorisez), pour afficher votre position
                  sur la carte et proposer des fonctionnalites de proximite.
                </li>
                <li>
                  <strong>Donnees techniques</strong> (type d'appareil, version du systeme, journaux d'erreurs)
                  pour assurer le bon fonctionnement et ameliorer l'Application.
                </li>
                <li>
                  <strong>Preferences d'utilisation</strong> (ex. options selectionnees, favoris, parametres)
                  pour personnaliser l'experience.
                </li>
                <li>
                  <strong>Donnees liees aux notifications</strong> (si vous activez les notifications) pour vous
                  envoyer des alertes liees au service.
                </li>
              </ul>
              <p>Nous ne collectons pas de donnees non necessaires au fonctionnement de l'Application.</p>
            </section>

            <section className="privacy-section">
              <h2>2. Finalites d'utilisation des donnees</h2>
              <p>Nous utilisons vos donnees pour :</p>
              <ul>
                <li>fournir les fonctionnalites de l'Application ;</li>
                <li>ameliorer la performance, la stabilite et l'experience utilisateur ;</li>
                <li>envoyer des notifications si vous y avez consenti ;</li>
                <li>assurer la securite et prevenir les usages frauduleux ;</li>
                <li>respecter nos obligations legales.</li>
              </ul>
            </section>

            <section className="privacy-section">
              <h2>3. Base legale du traitement</h2>
              <p>Selon les cas, les traitements reposent sur :</p>
              <ul>
                <li>votre <strong>consentement</strong> (ex. localisation, notifications) ;</li>
                <li>notre <strong>interet legitime</strong> (ex. amelioration et securite du service) ;</li>
                <li>le <strong>respect d'obligations legales</strong>.</li>
              </ul>
              <p>Vous pouvez retirer votre consentement a tout moment via les reglages de votre appareil.</p>
            </section>

            <section className="privacy-section">
              <h2>4. Partage des donnees</h2>
              <p>Nous ne vendons pas vos donnees personnelles.</p>
              <p>Vos donnees peuvent etre partagees uniquement avec :</p>
              <ul>
                <li>
                  nos prestataires techniques (hebergement, analytics, notifications), strictement pour operer
                  l'Application ;
                </li>
                <li>les autorites competentes, lorsque la loi l'exige.</li>
              </ul>
              <p>Ces prestataires sont soumis a des obligations de confidentialite et de securite.</p>
            </section>

            <section className="privacy-section">
              <h2>5. Conservation des donnees</h2>
              <p>
                Nous conservons les donnees uniquement pendant la duree necessaire aux finalites decrites ci-dessus,
                puis elles sont supprimees ou anonymisees, sauf obligation legale contraire.
              </p>
            </section>

            <section className="privacy-section">
              <h2>6. Securite</h2>
              <p>
                Nous mettons en oeuvre des mesures techniques et organisationnelles raisonnables pour proteger vos
                donnees contre l'acces non autorise, la perte, l'alteration ou la divulgation.
              </p>
              <p>
                Aucune methode de transmission ou de stockage n'etant totalement infaillible, nous ne pouvons garantir
                une securite absolue.
              </p>
            </section>

            <section className="privacy-section">
              <h2>7. Vos droits</h2>
              <p>
                Conformement a la reglementation applicable (notamment le RGPD, si applicable), vous disposez de
                droits :
              </p>
              <ul>
                <li>d'acces ;</li>
                <li>de rectification ;</li>
                <li>d'effacement ;</li>
                <li>de limitation ;</li>
                <li>d'opposition ;</li>
                <li>de portabilite.</li>
              </ul>
              <p>
                Vous pouvez exercer vos droits en nous contactant a :{' '}
                <a href="mailto:axel@flem.tv">axel@flem.tv</a>.
              </p>
              <p>Vous pouvez egalement introduire une reclamation aupres de l'autorite de controle competente.</p>
            </section>

            <section className="privacy-section">
              <h2>8. Donnees des mineurs</h2>
              <p>
                L'Application n'est pas destinee aux personnes de moins de 4 ans sans autorisation parentale.
              </p>
              <p>
                Si vous pensez qu'un mineur nous a transmis des donnees sans autorisation, contactez-nous pour
                suppression.
              </p>
            </section>

            <section className="privacy-section">
              <h2>9. Services tiers et liens externes</h2>
              <p>L'Application peut contenir des liens ou integrer des services tiers.</p>
              <p>
                Leur politique de confidentialite s'applique a leurs propres traitements. Nous vous invitons a les
                consulter.
              </p>
            </section>

            <section className="privacy-section">
              <h2>10. Modifications de la politique</h2>
              <p>Nous pouvons mettre a jour cette politique de confidentialite a tout moment.</p>
              <p>
                La version en vigueur est celle publiee dans l'Application, avec sa date de mise a jour.
              </p>
            </section>

            <section className="privacy-section">
              <h2>11. Contact</h2>
              <p>Pour toute question relative a cette politique ou a vos donnees personnelles :</p>
              <p>
                <strong>FLEM</strong>
                <br />898 Route de Nages, langlade
                <br />
                <a href="mailto:production@flem.tv">production@flem.tv</a>
              </p>
            </section>
          </article>
        </main>

        <footer className="footer">
          <p>© {new Date().getFullYear()} Linja. Suivi bus TanGo en temps reel.</p>
          {legalLinks}
        </footer>
      </div>
    );
  }

  if (isTermsPage) {
    return (
      <div className="app-shell">
        <div className="ambient-shape ambient-left" />
        <div className="ambient-shape ambient-right" />

        <header className="topbar">
          <a className="brand" href="/">
            <img src={assetPath('/logo/logo-rounded.png')} alt="Logo Linja" />
            <span>Linja</span>
          </a>
          <nav className="topbar-nav">
            <a href="/">Retour a l'accueil</a>
          </nav>
        </header>

        <main className="privacy-page">
          <article className="privacy-card">
            <h1>Terms of Use</h1>
            <p className="privacy-updated">
              <strong>Derniere mise a jour :</strong> 31/03/26
            </p>
            <p>
              Les presentes conditions d'utilisation definissent les regles applicables a l'utilisation de
              l'application <strong>Linja</strong>, editee par <strong>FLEM</strong>. En utilisant Linja, vous
              acceptez ces conditions.
            </p>

            <section className="privacy-section">
              <h2>1. Service propose</h2>
              <p>
                Linja permet de consulter des informations de transport (lignes, passages et suivi des bus) sur Nimes
                et les villages voisins de la metropole de Nimes.
              </p>
            </section>

            <section className="privacy-section">
              <h2>2. Compte et acces</h2>
              <p>
                Certaines fonctionnalites peuvent necessiter un acces a des services tiers (notamment Apple). Vous
                etes responsable des informations fournies et de l'usage de votre appareil.
              </p>
            </section>

            <section className="privacy-section">
              <h2>3. Abonnements Linja Premium</h2>
              <p>Linja propose un abonnement <strong>Linja Premium</strong> en deux formules :</p>
              <ul>
                <li><strong>Mensuel :</strong> 1,99 EUR / mois</li>
                <li><strong>Annuel :</strong> 17,99 EUR / an</li>
              </ul>
              <p>
                Aucun essai gratuit n'est propose. L'abonnement est automatiquement renouvele sauf annulation avant
                la date de renouvellement.
              </p>
            </section>

            <section className="privacy-section">
              <h2>4. Paiement, renouvellement et annulation</h2>
              <p>
                Le paiement est effectue via votre compte App Store, selon les conditions d'Apple. Le renouvellement
                automatique est active par defaut.
              </p>
              <p>
                Vous pouvez annuler votre abonnement a tout moment depuis l'application ou depuis les reglages de
                l'App Store.
              </p>
            </section>

            <section className="privacy-section">
              <h2>5. Remboursements</h2>
              <p>
                Pour toute demande de remboursement, contactez-nous a :{' '}
                <a href="mailto:linja@flem.tv">linja@flem.tv</a>.
              </p>
            </section>

            <section className="privacy-section">
              <h2>6. Usage autorise</h2>
              <p>
                Vous vous engagez a utiliser Linja de maniere legale et conforme a sa finalite. Toute utilisation
                abusive, tentative de perturbation du service ou atteinte a la securite peut entrainer une suspension
                d'acces.
              </p>
            </section>

            <section className="privacy-section">
              <h2>7. Disponibilite et responsabilite</h2>
              <p>
                Nous faisons notre maximum pour assurer la disponibilite et la fiabilite des informations. Toutefois,
                Linja est fournie "en l'etat", sans garantie d'absence totale d'erreur, d'interruption ou de retard
                de donnees.
              </p>
              <p>
                FLEM ne pourra etre tenue responsable des consequences liees a une indisponibilite temporaire du
                service ou a des donnees inexactes provenant de sources tierces.
              </p>
            </section>

            <section className="privacy-section">
              <h2>8. Propriete intellectuelle</h2>
              <p>
                Les contenus, marques, logos, interfaces et elements de Linja sont proteges par les droits de
                propriete intellectuelle. Toute reproduction non autorisee est interdite.
              </p>
            </section>

            <section className="privacy-section">
              <h2>9. Modifications</h2>
              <p>
                Nous pouvons mettre a jour les presentes conditions a tout moment. La version applicable est celle
                publiee dans l'application ou sur le site, avec sa date de mise a jour.
              </p>
            </section>

            <section className="privacy-section">
              <h2>10. Contact</h2>
              <p>Pour toute question relative aux conditions d'utilisation :</p>
              <p>
                <strong>FLEM</strong>
                <br />898 Route de Nages, langlade
                <br />
                <a href="mailto:linja@flem.tv">linja@flem.tv</a>
              </p>
            </section>
          </article>
        </main>

        <footer className="footer">
          <p>© {new Date().getFullYear()} Linja. Suivi bus TanGo en temps reel.</p>
          {legalLinks}
        </footer>
      </div>
    );
  }

  if (isDeleteAccountPage) {
    return (
      <div className="app-shell">
        <div className="ambient-shape ambient-left" />
        <div className="ambient-shape ambient-right" />

        <header className="topbar">
          <a className="brand" href="/">
            <img src={assetPath('/logo/logo-rounded.png')} alt="Logo Linja" />
            <span>Linja</span>
          </a>
          <nav className="topbar-nav">
            <a href="/">Retour a l'accueil</a>
          </nav>
        </header>

        <main className="privacy-page">
          <article className="privacy-card">
            <h1>Supprimer mon compte</h1>
            <p>
              Vous pouvez demander la suppression de votre compte <strong>Linja</strong> et de vos donnees
              personnelles associees a tout moment.
            </p>

            <section className="privacy-section">
              <h2>Comment demander la suppression</h2>
              <p>
                Envoyez un e-mail a{' '}
                <a href="mailto:production@flem.tv?subject=Suppression%20de%20mon%20compte%20Linja">
                  production@flem.tv
                </a>{' '}
                depuis l'adresse associee a votre compte, en indiquant votre demande de suppression de compte.
              </p>
              <p>
                Votre demande sera traitee dans les meilleurs delais, et au plus tard sous 30 jours.
              </p>
            </section>

            <section className="privacy-section">
              <h2>Donnees supprimees</h2>
              <p>Lorsque votre compte est supprime, nous effacons :</p>
              <ul>
                <li>vos informations de compte (identifiant, preferences) ;</li>
                <li>vos favoris et parametres personnalises ;</li>
                <li>vos donnees de localisation et d'utilisation associees a votre compte.</li>
              </ul>
              <p>
                Certaines donnees peuvent etre conservees plus longtemps si la loi nous y oblige (ex. obligations
                comptables ou legales), puis sont supprimees ou anonymisees.
              </p>
            </section>

            <section className="privacy-section">
              <h2>Contact</h2>
              <p>
                <strong>FLEM</strong>
                <br />898 Route de Nages, langlade
                <br />
                <a href="mailto:production@flem.tv">production@flem.tv</a>
              </p>
            </section>
          </article>
        </main>

        <footer className="footer">
          <p>© {new Date().getFullYear()} Linja. Suivi bus TanGo en temps reel.</p>
          {legalLinks}
        </footer>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <div className="ambient-shape ambient-left" />
      <div className="ambient-shape ambient-right" />

      <header className="topbar">
        <a className="brand" href="#accueil">
          <img src={assetPath('/logo/logo-rounded.png')} alt="Logo Linja" />
          <span>Linja</span>
        </a>

        <nav className="topbar-nav">
          <a href="#fonctionnalites">Fonctionnalites</a>
          <a href="#captures">Captures</a>
          <a href="#telecharger">Telecharger</a>
        </nav>
      </header>

      <main>
        <section className="hero" id="accueil">
          <p className="hero-kicker">Transport urbain a Nimes</p>
          <h1>Ne ratez plus votre bus TanGo.</h1>
          <p className="hero-copy">
            Linja remplace les horaires theoriques par des informations utiles en temps reel. Une application
            minimale, rapide et claire pour suivre uniquement les bus de Nimes et des villages voisins, simplement.
          </p>

          <div className="hero-actions">
            <a className="btn btn-primary" href="#telecharger">
              Telecharger l&apos;app
            </a>
          </div>

          <ul className="hero-metrics" aria-label="Points forts de Linja">
            <li>
              <strong>Temps reel</strong>
              <span>Suivi des bus en direct</span>
            </li>
            <li>
              <strong>Simplicite</strong>
              <span>Acces a l&apos;info en quelques secondes</span>
            </li>
            <li>
              <strong>Local</strong>
              <span>Nimes et villages autour uniquement</span>
            </li>
          </ul>

          <div className="hero-visual">
            <img
              src={assetPath('/images/67shots_so.png')}
              alt="Apercu de l'application Linja avec plusieurs ecrans"
            />
          </div>
        </section>

        <section className="section" id="fonctionnalites">
          <div className="section-head">
            <p>Pourquoi Linja</p>
            <h2>Le suivi TanGo repense avec priorite a la simplicite</h2>
          </div>
          <div className="feature-grid">
            {features.map((feature) => (
              <article key={feature.title} className="feature-card">
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section" id="captures">
          <div className="section-head">
            <p>Captures</p>
            <h2>Une interface minimaliste pour aller droit a l&apos;essentiel</h2>
          </div>

          <div className="showcase-grid">
            <img
              src={assetPath('/images/761shots_so.png')}
              alt="Application Linja en mode horaire et carte"
            />
            <img
              src={assetPath('/images/779shots_so.png')}
              alt="Ecran de suivi des horaires et des arrets sur Linja"
            />
          </div>
        </section>

        <section className="section">
          <div className="section-head">
            <p>Comment ca marche</p>
            <h2>Trois etapes pour gagner du temps chaque jour</h2>
          </div>
          <ol className="steps">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>

        <section className="section download" id="telecharger">
          <div className="section-head">
            <p>Telechargement</p>
            <h2>Installez Linja et reprenez le controle de vos trajets</h2>
          </div>

          <div className="download-actions">
            <a
              className="btn btn-primary"
              href="https://apps.apple.com/fr/app/linja/id6756112901"
              target="_blank"
              rel="noreferrer"
            >
              Telecharger sur l&apos;App Store
            </a>
            <a
              className="btn btn-outline"
              href="https://play.google.com/store/apps/details?id=app.flobus&pcampaignid=web_share"
              target="_blank"
              rel="noreferrer"
            >
              Telecharger sur Google Play
            </a>
          </div>
        </section>

        <section className="section faq" id="faq">
          <div className="section-head">
            <p>FAQ</p>
            <h2>Questions frequentes</h2>
          </div>
          {faqs.map((item) => (
            <details key={item.question}>
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </section>
      </main>

      <footer className="footer">
        <p>© {new Date().getFullYear()} Linja. Suivi bus TanGo en temps reel.</p>
        <div className="footer-links">
          <a href="/privacy">Politique de confidentialite</a>
          <a href="/terms-of-use">Terms of Use</a>
          <a href="/delete-account">Supprimer mon compte</a>
        </div>
      </footer>

      {showInstagramPopup ? (
        <div className="insta-popup-overlay" role="dialog" aria-modal="true" aria-label="Instagram Linja">
          <div className="insta-popup-card">
            <button type="button" className="insta-close" onClick={closeInstagramPopup} aria-label="Fermer">
              ×
            </button>

            <div className="insta-head">
              <span className="insta-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false">
                  <rect x="3" y="3" width="18" height="18" rx="5" ry="5" />
                  <circle cx="12" cy="12" r="4.2" />
                  <circle cx="17.2" cy="6.8" r="1.2" />
                </svg>
              </span>
              <p className="insta-eyebrow">Linja sur Instagram</p>
            </div>
            <h2>Ajoute-nous sur Insta</h2>
            <p>
              Suis les actus, les nouveautes et les infos produit en direct.
            </p>

            <div className="insta-actions">
              <a
                className="btn btn-primary insta-cta"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                onClick={closeInstagramPopup}
              >
                Voir Instagram
              </a>
              <button type="button" className="btn btn-ghost insta-later" onClick={closeInstagramPopup}>
                Plus tard
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default App;
